// src/features/layout/context/AuthLayoutContext.tsx
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useAuth } from '@clerk/tanstack-react-start';
import { useResolvedPathname } from '../hooks/useResolvedPathname';
import type { ReactNode } from 'react';

// Header height that layouts should reference
export const HEADER_HEIGHT = 70;

/**
 * Navigation variant determines what navigation to show in the header.
 * - `landing`: hash-link navigation for marketing/landing pages
 * - `authed`: application navigation for signed-in users
 */
export type NavVariant = 'landing' | 'authed';

export interface AuthLayoutContextValue {
  // Auth state from Clerk's useAuth (fast, no WebSocket latency)
  isAuthenticated: boolean;
  isAuthLoading: boolean;

  // Viewport
  isMobile: boolean;

  // Layout decisions (derived)
  showHeader: boolean;
  showFooter: boolean;
  footerHeight: number;

  // Header decisions (derived)
  navVariant: NavVariant;
  showProfileMenu: boolean;
}

const AuthLayoutContext = createContext<AuthLayoutContextValue | null>(null);

export interface AuthLayoutProviderProps {
  children: ReactNode;
}

/**
 * Provides centralized auth state and derived layout configuration.
 * 
 * Uses Clerk's useAuth() for fast UI auth state (no WebSocket latency).
 * This provides immediate feedback for layout changes on auth state changes.
 * 
 * Server-side auth is handled separately in __root.tsx beforeLoad using
 * Clerk's auth() server function and passed to Convex's serverHttpClient for SSR.
 * 
 * All layout decisions (header visibility, footer height, nav variant, etc.)
 * are computed once here and shared via context.
 */
export function AuthLayoutProvider({ children }: AuthLayoutProviderProps) {
  const theme = useMantineTheme();
  const resolvedPathname = useResolvedPathname();

  // Track hydration state to avoid SSR/client mismatch
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // useMediaQuery with SSR-safe defaults:
  // - initialValue: false ensures server render matches first client render
  // - getInitialValueInEffect: false means it uses initialValue during hydration
  // After hydration, the actual media query value is used
  const isMobileQuery = useMediaQuery(`(max-width: ${theme.breakpoints.md})`, false, {
    getInitialValueInEffect: false,
  });
  
  // Only use actual media query result after hydration to prevent mismatch
  const isMobile = isHydrated ? isMobileQuery : false;

  // useAuth from Clerk provides fast auth state for UI decisions
  // isLoaded: true when Clerk SDK has loaded (inverted from isLoading)
  // isSignedIn: true when user is authenticated
  const { isLoaded, isSignedIn } = useAuth();
  
  // Map to consistent variable names used throughout the app
  const isAuthLoading = !isLoaded;
  const isAuthenticated = isSignedIn ?? false;

  const value = useMemo<AuthLayoutContextValue>(() => {
    // While auth is loading, show unauthenticated layout to avoid flash
    const effectiveAuth = isAuthLoading ? false : isAuthenticated;
    
    // Layout visibility rules:
    // - Header hidden on mobile when authenticated (bottom nav used instead)
    // - Footer shown on mobile (always) and desktop (only when not authenticated)
    const showHeader = !(isMobile && effectiveAuth);
    const showFooter = isMobile || !effectiveAuth;

    // Footer height based on context
    const footerHeight = showFooter
      ? isMobile
        ? (theme.other.mobileFooterHeight as number)
        : 60
      : 0;

    // Navigation variant
    const navVariant: NavVariant = effectiveAuth ? 'authed' : 'landing';

    // Profile menu shown on desktop for authenticated users
    const showProfileMenu = effectiveAuth && !isMobile;

    return {
      isAuthenticated: effectiveAuth,
      isAuthLoading,
      isMobile,
      showHeader,
      showFooter,
      footerHeight,
      navVariant,
      showProfileMenu,
    };
  }, [isMobile, isAuthenticated, isAuthLoading, resolvedPathname, theme.other.mobileFooterHeight]);

  return (
    <AuthLayoutContext.Provider value={value}>
      {children}
    </AuthLayoutContext.Provider>
  );
}

/**
 * Hook to access auth state and layout configuration.
 * Must be used within an AuthLayoutProvider.
 */
export function useAuthLayout(): AuthLayoutContextValue {
  const context = useContext(AuthLayoutContext);
  if (!context) {
    throw new Error('useAuthLayout must be used within AuthLayoutProvider');
  }
  return context;
}
